//
// Copyright 2025 DXOS.org
//

import type * as Atom from 'effect/reactivity/Atom';

import { ObjectDeletedId } from '../common/types/model-symbols.ts';
import type { LoadOptions, Ref } from './ref.ts';

/** Reads the deletion marker off a value of unconstrained target type. */
export const isTargetDeleted = (target: unknown): boolean =>
  typeof target === 'object' && target !== null && (target as Record<symbol, unknown>)[ObjectDeletedId] === true;

/**
 * Internal helper for loading ref targets in atoms.
 * Handles the common pattern of checking for loaded target and triggering async load.
 */
export const loadRefTarget = <T, R>(
  ref: Ref<T>,
  get: Atom.AtomContext,
  onTargetAvailable: (target: T) => R,
  options?: LoadOptions,
): R | undefined => {
  // Accessing `ref.target` registers a resolution callback when the target is
  // not yet loaded, so resolution can be observed via `ref.onResolved` below.
  const currentTarget = ref.target;
  if (currentTarget) {
    return onTargetAvailable(currentTarget);
  }

  // Subscribe to the ref's resolution event in case the target loads later.
  const unsubscribe = ref.onResolved(() => {
    const target = ref.target;
    if (target) {
      get.setSelf(onTargetAvailable(target));
    }
  });
  get.addFinalizer(unsubscribe);

  // Also try async load (e.g. for objects that need disk loading). `ref.target` above never yields a
  // deleted target, so this is the only path that reaches one.
  void ref
    .load(options)
    .then((loadedTarget) => {
      get.setSelf(onTargetAvailable(loadedTarget));
    })
    .catch(() => {
      // Loading failed; the resolution subscription above will pick up cross-client updates.
    });

  return undefined;
};
